import { makeVar, useReactiveVar } from '@apollo/client';
import { localStorageService } from '@/services/local-storage-service';
import { getPersistedState, persistState } from '@/state-utils/persisted-state';

enum StreamDeckFocusMode {
  Normal = 'normal',
  Focus = 'focus',
}

const lsKey = 'stream-deck-settings';
const lsKeyFocusMode = 'stream-deck-focus-mode';

const streamDeckFocusModeState = getPersistedState(
  lsKeyFocusMode,
  StreamDeckFocusMode,
  StreamDeckFocusMode.Normal,
) as StreamDeckFocusMode;

export const streamDeckFocusMode = makeVar<StreamDeckFocusMode>(
  streamDeckFocusModeState,
);

export enum StreamDeckSettingName {
  CommentActions = 'Share Action',
  StreamLabel = 'Stream Label',
  ReputationBet = 'Reputation Bet',
  EnabledLinks = 'Enabled Links',
  PostInteractions = 'Viewing Post',
  CreatedDate = 'Created Date',
  CollapseLongPosts = 'Collapse Long Posts',
  ExpandOnHighlight = 'Expand on Highlight',
}

export const DEFAULT_COLLAPSED_BODY_CHARS = 300;
export const MIN_COLLAPSED_BODY_CHARS = 100;
export const MAX_COLLAPSED_BODY_CHARS = 2000;
export const COLLAPSED_BODY_CHARS_STEP = 50;

export type StreamDeckSettings = {
  commentActions: boolean;
  streamLabel: boolean;
  reputationBet: boolean;
  enabledLinks: boolean;
  postInteractions: boolean;
  createdDate: boolean;
  maxBodyChars: number | null;
  expandOnHighlight: boolean;
};

const defaultSettings: StreamDeckSettings = {
  commentActions: true,
  streamLabel: false,
  reputationBet: true,
  enabledLinks: true,
  postInteractions: true,
  createdDate: true,
  maxBodyChars: DEFAULT_COLLAPSED_BODY_CHARS,
  expandOnHighlight: true,
};

const focusModeSettings: StreamDeckSettings = {
  commentActions: false,
  streamLabel: false,
  reputationBet: false,
  enabledLinks: false,
  postInteractions: false,
  createdDate: false,
  maxBodyChars: DEFAULT_COLLAPSED_BODY_CHARS,
  expandOnHighlight: true,
};

type BooleanSettingKey = {
  [K in keyof StreamDeckSettings]: StreamDeckSettings[K] extends boolean
    ? K
    : never;
}[keyof StreamDeckSettings];

export const isStreamDeckSettingEnabled = (
  key: BooleanSettingKey,
  streamDeckSettings?: StreamDeckSettings,
): boolean => {
  const shouldSkipSettingCheck = !streamDeckSettings;
  if (shouldSkipSettingCheck) {
    return true;
  }

  return streamDeckSettings[key];
};

const sanitizeMaxBodyChars = (value: unknown): number | null => {
  if (value === null) return null;
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_COLLAPSED_BODY_CHARS;
  }
  if (value <= 0) return null;
  return Math.min(
    MAX_COLLAPSED_BODY_CHARS,
    Math.max(MIN_COLLAPSED_BODY_CHARS, value),
  );
};

const mergeWithDefaults = (parsed: unknown): StreamDeckSettings => {
  if (!parsed || typeof parsed !== 'object') return defaultSettings;
  const merged = {
    ...defaultSettings,
    ...(parsed as Partial<StreamDeckSettings>),
  };
  return { ...merged, maxBodyChars: sanitizeMaxBodyChars(merged.maxBodyChars) };
};

const getStreamDeckSettingsFromStorage = (): StreamDeckSettings => {
  try {
    const persisted = localStorageService.getItem(lsKey);
    return persisted
      ? mergeWithDefaults(JSON.parse(persisted))
      : defaultSettings;
  } catch {
    return defaultSettings;
  }
};

const streamDeckSettings = makeVar<StreamDeckSettings>(
  getStreamDeckSettingsFromStorage(),
);

export const useStreamDeckSettings = () => {
  const settings = useReactiveVar(streamDeckSettings);

  const isFocusModeOn =
    useReactiveVar(streamDeckFocusMode) === StreamDeckFocusMode.Focus;

  // Focus mode hides blocks on the News Deck only. It never reaches the saved
  // settings, so writes here and other surfaces are unaffected by it.
  const effectiveSettings = isFocusModeOn ? focusModeSettings : settings;

  const updateSetting = <K extends keyof StreamDeckSettings>(
    key: K,
    value: StreamDeckSettings[K],
  ) => {
    const newSettings = {
      ...settings,
      [key]: value,
    };
    persistState(lsKey, JSON.stringify(newSettings));
    streamDeckSettings(newSettings);
  };

  const switchFocusMode = () => {
    const newIsFocusModeOn = !isFocusModeOn;
    window.mixpanel?.track('Stream Deck Focus Mode Toggled', {
      'Focus Mode': newIsFocusModeOn ? 'On' : 'Off',
    });
    persistState(
      lsKeyFocusMode,
      newIsFocusModeOn ? StreamDeckFocusMode.Focus : StreamDeckFocusMode.Normal,
    );

    streamDeckFocusMode(
      newIsFocusModeOn ? StreamDeckFocusMode.Focus : StreamDeckFocusMode.Normal,
    );
  };

  return {
    settings,
    effectiveSettings,
    updateSetting,
    isFocusModeOn,
    switchFocusMode,
  };
};
